import type { Channel } from "@expert/shared-types";

import { Menu, Stack, Text } from "@mantine/core";
import { IconArrowsExchange } from "@tabler/icons-react";

import { useAnalytics } from "@/analytics";
import { env, switchChannel } from "@/config";
import { useAgentSdk, useAgentStore } from "@/sdk";
import { useLogger } from "@/sdk/logging/LoggerProvider";
import { clearPersistedWorkState } from "@/sdk/sessions/clearPersistedWorkState";
import { useFeatureFlag } from "@/shared-utils/hooks";

/**
 * Lets an expert whose routing profile can receive the other channel reload the workspace into it.
 * Only enabled while Offline, so no contact can arrive (and be ignored) mid-reload.
 */
export function SwitchChannelMenuItem() {
    const logger = useLogger({ module: "SwitchChannelMenuItem" });
    const { dispatcher } = useAnalytics();
    const agentSdk = useAgentSdk();
    const targetChannel: Channel = env.channel === "chat" ? "voice" : "chat";
    const canReceiveTargetChannel = useAgentStore((state) => (state.channelCapacity[targetChannel] ?? 0) > 0);
    const isOffline = useAgentStore((state) => state.activity === agentSdk.offlineAgentActivity);
    const { data: isChannelSwitchEnabled = false } = useFeatureFlag("EWP-isChannelSwitchEnabled");

    if (!isChannelSwitchEnabled || !canReceiveTargetChannel) return null;

    const onClick = async () => {
        logger.info({ fromChannel: env.channel, toChannel: targetChannel }, "Switching workspace channel");
        await dispatcher.dispatchBusinessEvent("WorkspaceChannelSwitched", {
            fromChannel: env.channel,
            toChannel: targetChannel,
        });
        clearPersistedWorkState();
        switchChannel(targetChannel);
    };

    return (
        <Menu.Item
            id="switch-channel-menu-item"
            leftSection={<IconArrowsExchange size={20} />}
            disabled={!isOffline}
            onClick={() => void onClick()}
        >
            <Stack gap={0}>
                <Text size="xs">Switch to {targetChannel} workspace</Text>
                {!isOffline && (
                    <Text size="xs" c="dimmed">
                        End your shift first
                    </Text>
                )}
            </Stack>
        </Menu.Item>
    );
}
